import { type ReactNode, useEffect, useSyncExternalStore } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'radar.theme';
const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

type ThemeState = { preference: ThemePreference; resolved: ResolvedTheme };

const listeners = new Set<() => void>();
let mediaQuery: MediaQueryList | null = null;
let state: ThemeState = { preference: 'system', resolved: 'light' };

function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
}

function getMediaQuery(): MediaQueryList | null {
  if (mediaQuery) return mediaQuery;
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  mediaQuery = window.matchMedia(DARK_MEDIA_QUERY);
  mediaQuery.addEventListener('change', onMediaChange);
  return mediaQuery;
}

function systemPrefersDark(): boolean {
  return getMediaQuery()?.matches ?? false;
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return preference;
}

function apply(preference: ThemePreference) {
  const resolved = resolveTheme(preference);
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  if (state.preference !== preference || state.resolved !== resolved) {
    state = { preference, resolved };
    for (const listener of listeners) listener();
  }
}

function onMediaChange() {
  if (state.preference === 'system') apply('system');
}

/** Relit la préférence stockée et l'applique (montage de l'application, tests). */
export function initTheme(): ThemeState {
  if (mediaQuery) {
    mediaQuery.removeEventListener('change', onMediaChange);
    mediaQuery = null;
  }
  apply(readPreference());
  return state;
}

export function getThemeState(): ThemeState {
  return state;
}

export function setThemePreference(preference: ThemePreference) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Stockage indisponible (navigation privée) : la préférence vaut pour la session.
  }
  apply(preference);
}

export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useTheme() {
  const current = useSyncExternalStore(subscribeTheme, getThemeState, getThemeState);
  return { theme: current.preference, resolved: current.resolved, setTheme: setThemePreference };
}

/** Applique le thème au montage ; `index.html` a déjà posé la classe avant le premier rendu. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    initTheme();
  }, []);
  return children;
}
